import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { getPortfolios, PortfolioOverviewItem } from '../../api/tax';
import { useTaxYearStore } from '../../stores/useTaxYearStore';
import { useAutoRefreshTrigger } from '../../stores/useGlobalAutoRefreshStore';
import DataTable from '../shared/DataTable';

/**
 * PortfoliosView — Overview tab.
 *
 * Small (~3 row) summary table from GET /v1/tax/portfolios. No pagination —
 * the endpoint always returns one row per registered portfolio. Sorting +
 * name search are client-side.
 */
export const PortfoliosView: React.FC = () => {
  const taxYear = useTaxYearStore((s) => s.selectedYear);
  const refreshTrigger = useAutoRefreshTrigger('tax-reporting');
  const [globalFilter, setGlobalFilter] = useState('');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['tax', 'portfolios', taxYear, refreshTrigger],
    queryFn: () => getPortfolios(taxYear),
  });

  const columns = useMemo<ColumnDef<PortfolioOverviewItem>[]>(
    () => [
      { accessorKey: 'display_name', header: 'Portfolio' },
      { accessorKey: 'environment', header: 'Environment' },
      { accessorKey: 'coinbase_transaction_count', header: 'Coinbase Tx' },
      { accessorKey: 'system_trade_count', header: 'System Trades' },
      { accessorKey: 'reconciled_count', header: 'Reconciled' },
      { accessorKey: 'tax_lot_count', header: 'Tax Lots' },
      { accessorKey: 'cost_basis_count', header: 'Cost Basis' },
    ],
    []
  );

  return (
    <div aria-label="Overview view">
      <DataTable<PortfolioOverviewItem>
        data={data?.items ?? []}
        columns={columns}
        defaultSorting={[{ id: 'display_name', desc: false }]}
        search={{ placeholder: 'Portfolio name', value: globalFilter, onChange: setGlobalFilter }}
        loadingMessage="Loading portfolios…"
        isLoading={isLoading}
        isError={isError}
        errorMessage={error instanceof Error ? error.message : 'Failed to load portfolios'}
        emptyMessage="No portfolios for this tax year."
      />
    </div>
  );
};

export default PortfoliosView;
